const ARTICLE_CACHE_BUSTER_KEY = "articleCacheBuster";
const ARTICLE_STORAGE_KEY_PATTERN = /(article|articles|searchResults|resultsCache)/i;
const CACHE_STORAGE_KEY_PATTERN = /(article|articles|search|api|runtime)/i;

const safeForEachStorageKey = (storage, callback) => {
  if (!storage) return;

  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      keys.push(storage.key(i));
    }

    keys.filter(Boolean).forEach(callback);
  } catch {
    // Storage can be unavailable in private browsing or blocked contexts.
  }
};

const removeMatchingStorageKeys = (storage) => {
  safeForEachStorageKey(storage, (key) => {
    if (key !== ARTICLE_CACHE_BUSTER_KEY && ARTICLE_STORAGE_KEY_PATTERN.test(key)) {
      try {
        storage.removeItem(key);
      } catch {
        // Ignore storage cleanup failures; login should continue.
      }
    }
  });
};

export const hasCurrentFilter = (filter) => (
  Boolean(filter) &&
  typeof filter === "object" &&
  !Array.isArray(filter) &&
  Object.keys(filter).length > 0
);

export const storeCurrentFilter = (filter) => {
  try {
    if (hasCurrentFilter(filter)) {
      localStorage.setItem("currentFilter", JSON.stringify(filter));
    } else {
      localStorage.removeItem("currentFilter");
    }
  } catch {
    // Ignore localStorage failures; server remains source of truth.
  }
};

export const getArticleCacheBuster = () => {
  try {
    return localStorage.getItem(ARTICLE_CACHE_BUSTER_KEY) || "";
  } catch {
    return "";
  }
};

export const clearArticleCache = async () => {
  const stamp = String(Date.now());

  removeMatchingStorageKeys(window.localStorage);
  removeMatchingStorageKeys(window.sessionStorage);

  try {
    localStorage.setItem(ARTICLE_CACHE_BUSTER_KEY, stamp);
  } catch {
    // Ignore localStorage failures; no-store fetches still protect the request.
  }

  if (window.caches && typeof window.caches.keys === "function") {
    try {
      const keys = await window.caches.keys();
      await Promise.all(
        keys
          .filter((key) => CACHE_STORAGE_KEY_PATTERN.test(key))
          .map((key) => window.caches.delete(key))
      );
    } catch {
      // CacheStorage cleanup is best-effort.
    }
  }

  try {
    window.dispatchEvent(new CustomEvent("articleCacheCleared", { detail: { stamp } }));
  } catch {
    // Older browsers may not support CustomEvent construction.
  }

  return stamp;
};

export const clearArticleCacheOnLogin = () => clearArticleCache();

// Preserve appearance preferences, but clear all account-specific session data.
export const clearAccountSession = () => {
  ['token', 'user', 'isAdmin', 'currentFilter'].forEach(key => localStorage.removeItem(key));
};

// Mounted values include autofill and always override a previously captured draft.
export const readLoginCredentials = (identifierInput, passwordInput, fallback) => ({
  identifier: identifierInput ? identifierInput.value : fallback.identifier,
  password: passwordInput ? passwordInput.value : fallback.password,
});
